import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
    fetchMembershipStatus,
    fetchServerLocations,
} from "../../redux/actions/catalogActions";
import { useWebSocketStore } from "../../stores/websocketStore";
import useLobbyViewModel from "../lobby/useLobbyViewModel";
import { normalizeLocationId } from "../lobby/lobbyPageUtils";
import { TIERS, TIER_LIMITS } from "../../constants/membership";
import { AVAILABLE_MAPS, AVAILABLE_WINGMAN_MAPS } from "../../constants/maps";
import {
    canToggleLocationSelection,
    canToggleMapSelection,
} from "../../utils/mapSelectionLimits";
import { toast } from "../../universal/toast";

const SELECTION_LIMIT_TOAST_MS = 5000;

function showSelectionLimitToast(message) {
    if (!message) return;
    toast.error(message, { durationMs: SELECTION_LIMIT_TOAST_MS });
}

export default function useLobbySettingsController({
    actions,
    catalog,
    settingsResetVersion = 0,
}) {
    const dispatch = useDispatch();
    const { sendOwnerAction, clientId } = useWebSocketStore();

    const {
        user,
        lobbyMapsSelected = [],
        lobbyWingmanMapsSelected = [],
        selectedServerLocations = [],
        matchmakingOptions,
        lobbyState,
        friends = [],
        currentLobby = null,
        currentLobbyCode = null,
        matchmakingStarted = false,
    } = catalog;

    const {
        canEditLobbySettings,
        lobbyOptionsToDisplay,
        queueMode,
        lobbyMapsToDisplay,
        lobbyServerLocationsToDisplay,
        selfMember,
        hostMember,
    } = useLobbyViewModel({
        catalog,
        user,
        currentLobby,
        currentLobbyCode,
        matchmakingOptions,
        lobbyMapsSelected,
        lobbyWingmanMapsSelected,
        selectedServerLocations,
        lobbyState,
        matchmakingStarted,
        friends,
    });

    const membershipTier = catalog.membership?.tier || TIERS.FREE;
    const tierLimits = TIER_LIMITS[membershipTier] || TIER_LIMITS[TIERS.FREE];
    const canRefineSelections = tierLimits.canEditMatchmakingSettings;
    const mapEditingDisabled =
        (!canEditLobbySettings && currentLobbyCode) || !canRefineSelections;
    const locationEditingDisabled =
        (!canEditLobbySettings && currentLobbyCode) || !canRefineSelections;
    const isMatchmakingEditingDisabled =
        membershipTier === TIERS.FREE ||
        (!canEditLobbySettings && currentLobbyCode);
    const enabledLocationCount = Array.isArray(catalog.serverLocations)
        ? catalog.serverLocations
              .map((location) =>
                  normalizeLocationId(location?.id ?? location?.awsRegion),
              )
              .filter(Boolean).length
        : 0;
    const isReadOnly =
        Boolean(currentLobbyCode) && !canEditLobbySettings;

    useEffect(() => {
        if (catalog?.user?.userId) {
            dispatch(fetchMembershipStatus(catalog.user.userId));
        }
    }, [catalog?.user?.userId, dispatch]);

    useEffect(() => {
        dispatch(fetchServerLocations());
    }, [dispatch, catalog?.user?.country]);

    const onMapClicked = (name) => {
        const decision = canToggleMapSelection({
            selectedMaps: lobbyMapsToDisplay,
            mapName: name,
            tier: membershipTier,
        });
        if (!decision.allowed) {
            showSelectionLimitToast(decision.message);
            return;
        }
        actions.lobbyMapClicked({
            name,
            userId: user.userId,
            queueMode,
        });
    };

    const onServerLocationClicked = (locationId) => {
        const decision = canToggleLocationSelection({
            selectedLocations: lobbyServerLocationsToDisplay,
            locationId,
            tier: membershipTier,
            availableCount: enabledLocationCount,
        });
        if (!decision.allowed) {
            showSelectionLimitToast(decision.message);
            return;
        }
        actions.lobbyServerLocationClicked({ locationId, userId: user.userId });
    };

    const handleLeaderMapUpdate = (mapName) => {
        const decision = canToggleMapSelection({
            selectedMaps: lobbyMapsToDisplay,
            mapName,
            tier: membershipTier,
        });
        if (!decision.allowed) {
            showSelectionLimitToast(decision.message);
            return;
        }

        actions.lobbyLeaderMapToggled({
            mapName,
            fallbackMaps: lobbyMapsToDisplay,
        });

        sendOwnerAction({
            messageId: "lobby-map-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            mapName,
            clientId,
        });
    };

    const handleLeaderServerLocationUpdate = (locationId) => {
        const decision = canToggleLocationSelection({
            selectedLocations: lobbyServerLocationsToDisplay,
            locationId,
            tier: membershipTier,
            availableCount: enabledLocationCount,
        });
        if (!decision.allowed) {
            showSelectionLimitToast(decision.message);
            return;
        }

        actions.lobbyLeaderServerLocationToggled({
            locationId,
            fallbackLocations: lobbyServerLocationsToDisplay,
        });

        sendOwnerAction({
            messageId: "lobby-server-location-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            locationId,
            clientId,
        });
    };

    const handleMatchmakingOptionChange = (settingData) => {
        if (
            settingData.name === "restrictServerLocationsToArea" &&
            settingData.enabled === true
        ) {
            const localLocationIds = (catalog.serverLocations || [])
                .filter((location) => location.isInArea !== false)
                .map((location) => normalizeLocationId(location.id))
                .filter(Boolean);
            actions.setSelectedServerLocations(
                lobbyServerLocationsToDisplay.filter((locationId) =>
                    localLocationIds.includes(normalizeLocationId(locationId)),
                ),
            );
        }

        actions.lobbyMatchmakingOptionChanged({
            name: settingData.name,
            enabled: settingData.enabled,
            value: settingData.value,
            hasSlider: settingData.hasSlider,
            listValue: settingData.listValue,
            userId: user.userId,
        });
    };

    const handleLeaderMatchmakingUpdate = (settingData) => {
        if (
            settingData.name === "restrictServerLocationsToArea" &&
            settingData.enabled === true
        ) {
            const localLocationIds = (catalog.serverLocations || [])
                .filter((location) => location.isInArea !== false)
                .map((location) => normalizeLocationId(location.id))
                .filter(Boolean);
            actions.lobbyLeaderUpdatedServerLocations({
                lobbyCode: currentLobbyCode,
                lobbyServerLocationsSelected: lobbyServerLocationsToDisplay.filter(
                    (locationId) =>
                        localLocationIds.includes(normalizeLocationId(locationId)),
                ),
            });
        }

        actions.lobbyLeaderMatchmakingOptionChanged({
            name: settingData.name,
            enabled: settingData.enabled,
            value: settingData.value,
            hasSlider: settingData.hasSlider,
            listValue: settingData.listValue,
        });

        sendOwnerAction({
            messageId: "lobby-options-update",
            lobbyCode: currentLobbyCode,
            userId: user.userId,
            name: settingData.name,
            enabled: settingData.enabled,
            value: settingData.value,
            hasSlider: settingData.hasSlider,
            listValue: settingData.listValue,
            clientId,
        });
    };

    return {
        availableMaps: queueMode === "wingman" ? AVAILABLE_WINGMAN_MAPS : AVAILABLE_MAPS,
        queueMode,
        canEditLobbySettings,
        canRefineSelections,
        currentLobbyCode,
        currentUserGender: selfMember?.gender || user?.gender || null,
        playerCountry:
            (currentLobbyCode && hostMember?.country) ||
            selfMember?.country ||
            user?.country ||
            null,
        isMatchmakingEditingDisabled,
        isReadOnly,
        lobbyMapsToDisplay,
        lobbyOptionsToDisplay,
        lobbyServerLocationsToDisplay,
        locationEditingDisabled,
        mapEditingDisabled,
        membershipTier,
        serverLocations: catalog.serverLocations,
        settingsResetVersion,
        handleLeaderMapUpdate,
        handleLeaderMatchmakingUpdate,
        handleLeaderServerLocationUpdate,
        handleMatchmakingOptionChange,
        onMapClicked,
        onServerLocationClicked,
    };
}
